import React, { useState } from 'react';
import { LinkIcon, PlusIcon } from '@heroicons/react/24/outline';
import Popup from '../../../components/general/Popup.tsx';
import { cn } from '../../../lib/utils.ts';

export type FixedAssetChoice = 'create' | 'link';

type Props = {
    open: boolean;
    linkableCount: number;
    isLoadingLinkable: boolean;
    onClose: () => void;
    onChoose: (choice: FixedAssetChoice) => void;
};

function linkDescription(isLoading: boolean, linkableCount: number): string {
    if (isLoading) {
        return 'Vaste activa worden geladen.';
    }
    if (linkableCount === 0) {
        return 'Deze klant heeft geen niet-gekoppelde vaste activa.';
    }
    return linkableCount === 1
        ? 'Kies uit 1 niet-gekoppeld vast actief van deze klant.'
        : `Kies uit ${linkableCount} niet-gekoppelde vaste activa van deze klant.`;
}

export function FixedAssetChoicePopup({ open, linkableCount, isLoadingLinkable, onClose, onChoose }: Props) {
    const [choice, setChoice] = useState<FixedAssetChoice>('create');
    const canLink = !isLoadingLinkable && linkableCount > 0;
    const selected = choice === 'link' && !canLink ? 'create' : choice;

    const options = [
        {
            value: 'create' as const,
            title: 'Nieuw vast actief aanmaken',
            description: 'Omschrijving, datum en aanschafwaarde worden vooraf ingevuld vanuit deze factuur.',
            Icon: PlusIcon,
            disabled: false,
        },
        {
            value: 'link' as const,
            title: 'Bestaand vast actief koppelen',
            description: linkDescription(isLoadingLinkable, linkableCount),
            Icon: LinkIcon,
            disabled: !canLink,
        },
    ];

    return (
        <Popup
            open={open}
            onClose={onClose}
            onConfirm={() => onChoose(selected)}
            title="Vast actief voor deze factuur"
            confirmButtonDisplayLabel="Verder"
            Icon={null}
        >
            <div className="space-y-3 py-2">
                <p className="text-sm text-gray-600">
                    Alle regels van deze factuur worden op de activarekening van het vaste actief geboekt.
                </p>
                <fieldset className="space-y-2">
                    <legend className="sr-only">Kies een manier</legend>
                    {options.map(({ value, title, description, Icon, disabled }) => (
                        <label
                            key={value}
                            data-testid={`fixed-asset-choice-${value}`}
                            className={cn(
                                'flex items-start gap-3 rounded-xl border p-3',
                                selected === value ? 'border-primary bg-superliteprimary' : 'border-gray-200',
                                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                            )}
                        >
                            <input
                                type="radio"
                                name="fixed-asset-choice"
                                value={value}
                                checked={selected === value}
                                disabled={disabled}
                                onChange={() => setChoice(value)}
                                className="mt-1"
                            />
                            <Icon className="mt-0.5 h-5 w-5 shrink-0 text-gray-600" aria-hidden="true" />
                            <span className="flex flex-col gap-0.5">
                                <span className="text-sm font-semibold text-gray-900">{title}</span>
                                <span className="text-xs text-gray-600">{description}</span>
                            </span>
                        </label>
                    ))}
                </fieldset>
            </div>
        </Popup>
    );
}
